<template>
    <v-container fluid class="pa-0">
        <v-form
            ref="form"
            @submit="onSubmit"
            @submit.prevent="onSubmit">

            <v-card-title class="d-flex align-center px-0">
                <span>{{ isEdit ? 'Edit' : 'Add' }} Packing Rule</span>
                <v-spacer></v-spacer>
                <v-switch
                    v-model="formData.enabled"
                    class="mr-4"
                    label="Enabled">
                </v-switch>
                <v-btn small class="mr-1" @click="closeForm">
                    Close
                </v-btn>
                <v-btn :disabled="isSaving" small type="submit" color="primary">
                    <span v-if="isSaving">
                        <v-progress-circular
                            indeterminate
                            size="12"
                            width="2"
                            color="primary darken-3">
                        </v-progress-circular>
                        Saving Group Packing..
                    </span>
                    <span v-else>Save</span>
                </v-btn>
            </v-card-title>

            <v-row no-gutters>
                <v-col cols="12">
                    <v-text-field
                        v-model="formData.label"
                        label="Rule Label"
                        class="mb-4">
                    </v-text-field>
                </v-col>
            </v-row>

            <v-row no-gutters>
                <v-col cols="12">
                    <v-textarea
                        v-model="formData.description"
                        label="Rule Description"
                        class="mb-4">
                    </v-textarea>
                </v-col>
            </v-row>

            <v-row no-gutters>
                <v-col cols="12">
                    <div class="mb-2">Target Items</div>
                    <v-radio-group :disabled="isTargetItemsLocked" v-model="formData.target" row class="mt-0">
                        <v-radio
                            label="Unpacked items"
                            value="unpacked"
                        ></v-radio>
                        <v-radio
                            label="All items (Including items that are packed)"
                            value="all"
                        ></v-radio>
                    </v-radio-group>
                </v-col>
            </v-row>

            <v-row no-gutters v-for="(conditions, ruleIndex) in formData.rule_conditions"
                   :key="ruleIndex"
                   class="mb-4">
                <v-col cols="12">
                    <v-card>
                        <v-card-title class="d-flex justify-between align-center">
                            <span>{{ ruleIndex == 0 ? 'Rule Conditions' : 'AND' }}</span>
                            <v-btn
                                icon
                                class="ml-auto close-btn"
                                @click="removeRuleCondition(ruleIndex)">
                                <i class="fa fa-times-circle fa-2x"></i>
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <div class="subtitle-1">Any of these conditions need to be true...</div>
                            <RuleConditionsForm
                                :conditions="conditions"
                                @updateRuleCondition="updateRuleCondition" />
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row no-gutters>
                <v-col cols="12">
                    <div class="my-5">
                        <v-btn small @click="addRuleCondition">Add Rule</v-btn>
                    </div>
                </v-col>
            </v-row>

            <v-col v-if="formData.rule_conditions && formData.rule_conditions.length > 0">
                <div class="mb-2">Which products should be included?</div>
                <v-radio-group class="mt-0" v-model="formData.products_to_pack">
                    <v-radio
                        :label="ruleSelectionOptions.MATCHED.label"
                        :value="ruleSelectionOptions.MATCHED.value"
                    ></v-radio>
                    <v-radio
                        :label="ruleSelectionOptions.UNPACKED.label"
                        :value="ruleSelectionOptions.UNPACKED.value"
                    ></v-radio>
                </v-radio-group>
            </v-col>

            <v-row no-gutters>
                <v-col cols="12">
                    <v-card-title class="px-0">Packing Method</v-card-title>
                    <v-card class="my-4">
                        <v-container fluid>
                            <v-row class="py-4">
                                <v-col cols="4">
                                    <v-select
                                        :items="packingMethods"
                                        v-model="formData.packing_method"
                                        dense
                                        outlined
                                        @change="lockTargetAllItems()">
                                        >
                                    </v-select>
                                </v-col>
                                <v-col cols="8">
                                    <v-alert
                                        v-if="formData.packing_method"
                                        outlined
                                        dense
                                        type="info"
                                        border="left">
                                        {{ getRuleDescription(formData.packing_method) }}
                                    </v-alert>
                                </v-col>

                                <v-col>
                                    <DimensionalPackingForm ref="formFields" v-if="formData.packing_method === 'dimensional-packing'"
                                        @validation-state="updateValidationState"
                                        @scroll-bottom="scrollBottom"
                                        @add-package-to-delete-list="addPackageToDeleteList"
                                        :itemTypes="itemTypes"
                                        :formData="formData" />

                                    <WeightPackingForm ref="formFields" v-else-if="formData.packing_method === 'weight-packing'"
                                        @validation-state="updateValidationState"
                                        @scroll-bottom="scrollBottom"
                                        @add-package-to-delete-list="addPackageToDeleteList"
                                        :itemTypes="itemTypes"
                                        :formData="formData" />

                                    <QuantityPackingForm ref="formFields" v-else-if="formData.packing_method === 'quantity-packing'"
                                        @validation-state="updateValidationState"
                                        :itemTypes="itemTypes"
                                        :formData="formData" />

                                    <ExistingPalletSkidPackingForm v-else-if="formData.packing_method === 'add-to-existing-pallet-skid'"
                                        :itemTypes="itemTypes"
                                        :formData="formData" />
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card>
                </v-col>
            </v-row>

            <v-row no-gutters ref="bottomAnchor">
                <v-col cols="12">
                    <div class="d-flex justify-end py-5 align-center">
                        <v-switch
                            v-model="formData.enabled"
                            class="mr-4"
                            label="Enabled">
                        </v-switch>
                        <v-btn small class="mr-1" @click="closeForm">
                            Close
                        </v-btn>
                        <v-btn :disabled="isSaving" small type="submit" color="primary">
                            <span v-if="isSaving">
                                <v-progress-circular
                                    indeterminate
                                    size="12"
                                    width="2"
                                    color="primary darken-3">
                                </v-progress-circular>
                                Saving...
                            </span>
                            <span v-else>Save</span>
                        </v-btn>
                    </div>
                </v-col>
            </v-row>

        </v-form>
    </v-container>
</template>

<style scoped>
    .close-btn:hover {
        color: red; /* Changes the icon color to red on hover */
    }
</style>

<script>
import { update } from 'lodash';
import { EventBus } from '@/js/event-bus.js'
import RuleConditionsForm from './RuleConditionsForm.vue';
import PackingRuleStruct from './PackingRuleStruct.js';
import DimensionalPackingForm from './settings/DimensionalPackingForm.vue';
import WeightPackingForm from './settings/WeightPackingForm.vue';
import QuantityPackingForm from './settings/QuantityPackingForm.vue';
import ExistingPalletSkidPackingForm from './settings/ExistingPalletSkidPackingForm.vue';
import { PACKING_RULES_SELECTION, PACKING_METHODS, WEIGHT_PACKING, DIMENSIONAL_PACKING, QUANTITY_PACKING } from '@/js/constants/packing-rules';
import { mapGetters } from 'vuex'

export default {

    props: {
        integrationId: Number,
        packingRuleIndex: Number,
        packingRules: Array,
        isEdit: Boolean,
    },
    components: {
        RuleConditionsForm,
        DimensionalPackingForm,
        WeightPackingForm,
        QuantityPackingForm,
        ExistingPalletSkidPackingForm
    },
    data() {
        return {
            isSaving: false,
            packingMethods: PACKING_METHODS,
            ruleSelectionOptions: PACKING_RULES_SELECTION,
            formData: {},
            itemTypes: [],
            hovering: false,
            isFormValid: true,

            oldTargetItemsValue: 'unpacked',
            isTargetItemsLocked: false,

            // store the packages that are to be deleted
            packagesToDelete: [], // array of package ids
        }
    },
    mounted() {

        if(this.isEdit) {
            this.formData = new PackingRuleStruct(this.packingRules[this.packingRuleIndex]);
            this.oldTargetItemsValue = this.formData.target;
        } else {
            // initialize the new form data
            this.formData = new PackingRuleStruct({
                integration_id: this.integrationId,
                order: this.packingRules.length + 1 // set the order value to the next available order
            });
        }

        this.lockTargetAllItems()
    },

    computed: {
        ...mapGetters('LRSettingsState', {
            shopifyUserId: 'getShopifyUserId'
        })
    },

    created() {
        this.fetchMSBoxItems();
    },

    methods: {

        lockTargetAllItems() {
            if(this.formData.packing_method === 'add-to-existing-pallet-skid') {
                this.oldTargetItemsValue = this.formData.target;
                this.formData.target = 'all';
                this.isTargetItemsLocked = true;
                return;
            }

            this.formData.target = this.oldTargetItemsValue;
            this.isTargetItemsLocked = false;
        },

        addRuleCondition() {
            this.formData.rule_conditions.push([{}]);
        },

        removeRuleCondition(index) {
            this.formData.rule_conditions.splice(index, 1);
        },

        updateRuleCondition(index, conditions) {
            this.formData.rule_conditions = update(this.formData.rule_conditions, {
                [index]: {
                    $set: conditions
                }
            });

        },

        getRuleDescription(value) {
            const option = this.packingMethods.find(opt => opt.value === value);
            return option ? option.description : '';
        },

        onSubmit:_.debounce(function(e) {
            e.preventDefault()

            if (this.isSaving) return

            // check first if the form requires validation, if yes then validate
            if(this.$refs.formFields && this.$refs.formFields.validate) {
                this.$refs.formFields.validate()
            } else {
                this.isFormValid = true
            }

            if(this.formData.packages.length > 500) {
                EventBus.$emit('show-error', 'Maximum of 500 items allowed.')
                this.isSaving = false
                return
            }

            if (!this.isFormValid) {
                EventBus.$emit('show-error', 'Form is invalid. Please check the fields.')
                this.isSaving = false
                return
            }

            this.isSaving = true

            const requiresPackages = [WEIGHT_PACKING, DIMENSIONAL_PACKING, QUANTITY_PACKING].includes(this.formData.packing_method);
            if(!requiresPackages) {
                // unset packages key from formData if the packing method does not require packages
                delete this.formData.packages;
            }

            let updateUrl = `/api/packing-rules`
            if (this.isEdit) updateUrl += `/${this.formData.id}`

            const formData = this.formData;
            formData.packagesToDelete = this.packagesToDelete;
            formData.client_id = this.shopifyUserId;

            this.$http
                .post(updateUrl, formData)
                .then(({ data }) => {
                    EventBus.$emit('show-success', 'Successfully saved packing group.')

                    const packingRule = new PackingRuleStruct(data.data);

                    if (this.isEdit) {
                        this.packingRules[this.packingRuleIndex] = packingRule
                    } else {
                        this.packingRules.push(packingRule)
                    }
                    this.closeForm()
                })
                .catch(err => {
                    EventBus.$emit('show-error', err)
                })
                .finally(() => {
                    this.isSaving = false
                })
        }, 200),

        closeForm() {
            this.$emit('closePackingRuleForm')
        },

        fetchMSBoxItems() {
            if (!this.integrationId) {
                return
            }

            let params = { integration_id: this.integrationId }

            this.$http
                .get('/api/machship/item-types', { params })
                .then(({ data }) => {
                    this.itemTypes = Object.keys(data.data).map(key => {
                        return {
                            text: data.data[key],
                            value: key
                        }
                    });
                })
        },

        // This method is called by the child components to update the validation state of the form
        updateValidationState(isValid) {
            this.isFormValid = isValid
        },

        scrollBottom() {
            this.$nextTick(() => {
                this.$refs.bottomAnchor.scrollIntoView({ behavior: "smooth" });
            });
        },

        addPackageToDeleteList(packageId) {

            // check if packageId is an array, replace the whole packagesToDelete array
            if (Array.isArray(packageId)) {
                this.packagesToDelete = packageId;
                return;
            }
            this.packagesToDelete.push(packageId);
        }
    }
}
</script>